import { List, Space, Tag, Typography } from 'antd';
import { isEmpty } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type * as api from '../../../api';
import { useCompany, useCompanyContext } from '../../../components/contexts/CompanyContextManager';
import CollapsibleWrapper, { Panel } from '../../../components/wrappers/CollapsibleWrapper';
import RoleTags from '../RoleDetailsPage/RoleTags';
import { PermissionOriginTags } from './PermissionOriginTags';
import UserPermissionEmptyList from './UserPermissionEmptyList';

export type UserPermissionListProps = { user: api.UserSpecsFragment };

const UserPermissionList = ({ user }: UserPermissionListProps) => {
    const { t } = useTranslation(['userDetails']);
    const company = useCompany(true);
    const companyContext = useCompanyContext();
    const availableCompanyIds = useMemo(
        () => companyContext?.availableCompanies.map(({ id }) => id) ?? [],
        [companyContext]
    );
    const { permissionWithOrigins } = user;
    const companyTagLabel = 'Company:';
    const companyTag = (displayName: string) => (
        <Tag color="green">
            <Space>
                <Typography.Text style={{ color: 'inherit' }} strong>
                    {companyTagLabel}
                </Typography.Text>
                <Typography.Text style={{ color: 'inherit' }}>{displayName}</Typography.Text>
            </Space>
        </Tag>
    );

    if (permissionWithOrigins.length === 0) {
        return <UserPermissionEmptyList user={user} />;
    }

    return (
        <CollapsibleWrapper defaultActiveKey={t('userDetails:cardTitles.permissionList')}>
            <Panel key={t('userDetails:cardTitles.permissionList')} header={t('userDetails:cardTitles.permissionList')}>
                <List size="small">
                    {permissionWithOrigins.map(({ permission, originRoles }) =>
                        // global permissions
                        isEmpty(permission.company?.id) ||
                        // all companies selected
                        (isEmpty(company?.id) && availableCompanyIds.includes(permission.company.id)) ||
                        // selected company
                        company.id === permission.company.id ? (
                            <List.Item key={permission.id}>
                                <List.Item.Meta
                                    description={
                                        <Space direction="vertical" style={{ width: '100%' }}>
                                            <span>{permission.description}</span>
                                            {permission.company && companyTag(permission.company.displayName)}
                                            <RoleTags tags={permission.tags} />
                                            <PermissionOriginTags origins={originRoles} />
                                        </Space>
                                    }
                                    title={permission.displayName}
                                />
                            </List.Item>
                        ) : null
                    )}
                </List>
            </Panel>
        </CollapsibleWrapper>
    );
};

export default UserPermissionList;
